import { SeededRng, type Size, type Vector2 } from '@sorrem/shared';
import type { UISkillTreeView } from '@sorrem-client/types';
import type { SkillTreeEffectTarget } from './skill-tree-ids';
import { createSoftDotSprite, fitCanvasToViewport, type SoftDotStop } from './skill-tree-canvas';
import { clamp01, wrap } from './skill-tree-math';
import { effectColorToRgb } from './skill-tree-palette';

/** Linear RGB, 0 to 1 per channel. */
export type SkillTreeEffectColor = readonly [number, number, number];

interface SkillTreeEffectBase {
    /** Stable id; it seeds the effect's phases and flicker, so an effect looks the same every time it is built. */
    readonly id: string;
    /** The part of the tree it lights, which times it during the intro (see `skill-tree-effects.ts`). */
    readonly target: SkillTreeEffectTarget;
    readonly color: SkillTreeEffectColor;
    /** Peak opacity, 0 to 1. */
    readonly alpha: number;
    /** Seconds after the effects are set before this one fades in (the Skills tab intro); none shows it at once. */
    readonly delay?: number;
}

/** A soft light that slowly breathes, centered on a node, dial, or the sigil. */
export interface SkillTreeHaloEffect extends SkillTreeEffectBase {
    readonly kind: 'halo';
    readonly center: Vector2;
    readonly radius: number;
}

/** Points of light that run along a path, fading at the ends of an open one. */
export interface SkillTreeGlintEffect extends SkillTreeEffectBase {
    readonly kind: 'glint';
    readonly path: readonly Vector2[];
    readonly closed: boolean;
    readonly count: number;
    /** Diameter of each glint's bright core, in tree units. */
    readonly size: number;
    /** Tree units per second. */
    readonly speed: number;
}

/** A path burning with flickering light and a hot core, shedding embers away from `center`. */
export interface SkillTreeBlazeEffect extends SkillTreeEffectBase {
    readonly kind: 'blaze';
    readonly path: readonly Vector2[];
    readonly closed: boolean;
    readonly center: Vector2;
    readonly hotColor: SkillTreeEffectColor;
    /** Diameter of each flame dot, in tree units. */
    readonly size: number;
    /** Distance between flame dots along the path, in tree units. */
    readonly spacing: number;
    readonly embers: number;
}

/** Motes drifting in slow circles around a center. */
export interface SkillTreeOrbitEffect extends SkillTreeEffectBase {
    readonly kind: 'orbit';
    readonly center: Vector2;
    readonly radius: number;
    readonly count: number;
    readonly size: number;
}

/** A one-shot flash: a ring of light expanding from `center` with sparks flying out, starting at `delay` and fading over `duration`. */
export interface SkillTreeBurstEffect extends SkillTreeEffectBase {
    readonly kind: 'burst';
    readonly center: Vector2;
    /** How far the ring expands, in tree units. */
    readonly radius: number;
    readonly hotColor: SkillTreeEffectColor;
    readonly sparks: number;
    /** Seconds. */
    readonly duration: number;
}

/**
 * One animated effect on the skill tree, in tree units (y grows downward, as
 * in the tree's SVG). `skill-tree-effects.ts` decides which effects each
 * node, dial, and the sigil gets; the renderer only animates them.
 */
export type SkillTreeEffect = SkillTreeHaloEffect | SkillTreeGlintEffect | SkillTreeBlazeEffect | SkillTreeOrbitEffect | SkillTreeBurstEffect;

/** Writes one dot: position in tree units, diameter in tree units, its color's sprite, and opacity. */
type DotWriter = (x: number, y: number, size: number, sprite: HTMLCanvasElement, alpha: number) => void;

/** The soft dot sprite in a color, drawn once and shared by every emitter that uses it. */
type SpriteFor = (color: SkillTreeEffectColor) => HTMLCanvasElement;

interface Emitter {
    /** Fades the emitter in this long after the effects were set; see `SkillTreeEffectBase.delay`. */
    readonly delay: number | undefined;
    /** `seconds` drives the looping animation; `sinceSet` is the time since the effects were set, for one-shot timing. */
    readonly emit: (seconds: number, write: DotWriter, sinceSet: number) => void;
}

const WHITE: SkillTreeEffectColor = [1, 1, 1];
const HALO_BREATH_HZ = 0.22;
const EMBER_RISE = 14;
/** How long a delayed effect takes to fade in, in seconds. */
const DELAYED_FADE_IN = 0.4;
const BURST_RING_DOTS = 44;
/** Resolution of the pre-drawn soft dot, in canvas pixels. */
const DOT_SPRITE_SIZE = 64;
/** Dots smaller than this on screen, in device pixels, are skipped. */
const MIN_DOT_PIXELS = 0.5;
/**
 * The soft dot's falloff, like the light it stands for: full at the center,
 * about half a quarter of the way out, and nothing at the edge.
 */
const DOT_SPRITE_STOPS: readonly SoftDotStop[] = [
    { offset: 0, alpha: 1 },
    { offset: 0.25, alpha: 0.55 },
    { offset: 0.6, alpha: 0.15 },
    { offset: 1, alpha: 0 },
];

/**
 * The skill tree's animated light, drawn on a transparent 2D canvas over the
 * tree's SVG: breathing halos, glints running along lit rings and spirals,
 * blazing mastered rings, and drifting motes.
 *
 * Every effect is built from one soft dot, pre-drawn once per color and
 * stamped with additive blending, so overlapping light adds up like a glow.
 * Positions are in tree units, mapped to the screen by the tree's own
 * pan/zoom. Phases and flicker come from `SeededRng`, seeded by effect id.
 */
export class SkillTreeEffectsRenderer {
    private emitters: Emitter[] = [];
    private readonly sprites = new Map<string, HTMLCanvasElement>();
    private readonly spriteFor: SpriteFor = (color) => this.sprite(color);
    private elapsedSeconds = 0;
    /** `elapsedSeconds` when the current effects were set. */
    private setAtSeconds = 0;

    /** Replaces every effect; delays and bursts count from now. */
    setEffects(effects: readonly SkillTreeEffect[]): void {
        this.emitters = effects.map((effect) => createEmitter(effect, this.spriteFor));
        this.setAtSeconds = this.elapsedSeconds;
    }

    /** Advances every effect. Call every frame with the elapsed seconds; skip it to hold them still. */
    update(deltaSeconds: number): void {
        this.elapsedSeconds += deltaSeconds;
    }

    /** Draws the current frame: `view` puts tree point `view.center` at the middle of `viewport` (CSS pixels), at `view.zoom` CSS pixels per tree unit. */
    draw(canvas: HTMLCanvasElement, view: UISkillTreeView, viewport: Size): void {
        const fitted = fitCanvasToViewport(canvas, viewport);
        if (!fitted) return;
        const { context, pixelRatio, width, height } = fitted;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, width, height);
        context.globalCompositeOperation = 'lighter';

        const scale = view.zoom * pixelRatio;
        const offsetX = width / 2 - view.center.x * scale;
        const offsetY = height / 2 - view.center.y * scale;
        /** The current emitter's delayed fade-in, applied by `write` so delayed emitters need no wrapper of their own. */
        let fade = 1;
        const write: DotWriter = (x, y, size, sprite, alpha) => {
            const opacity = Math.min(1, alpha * fade);
            const pixels = size * scale;
            if (opacity <= 0 || pixels < MIN_DOT_PIXELS) return;
            const screenX = x * scale + offsetX;
            const screenY = y * scale + offsetY;
            const half = pixels / 2;
            if (screenX + half < 0 || screenY + half < 0 || screenX - half > width || screenY - half > height) return;
            context.globalAlpha = opacity;
            context.drawImage(sprite, screenX - half, screenY - half, pixels, pixels);
        };

        const sinceSet = this.elapsedSeconds - this.setAtSeconds;
        for (const emitter of this.emitters) {
            fade = emitter.delay === undefined ? 1 : clamp01((sinceSet - emitter.delay) / DELAYED_FADE_IN);
            if (fade <= 0) continue;
            emitter.emit(this.elapsedSeconds, write, sinceSet);
        }
        context.globalAlpha = 1;
        context.globalCompositeOperation = 'source-over';
    }

    /** The soft dot in `color`, drawn once and reused. */
    private sprite(color: SkillTreeEffectColor): HTMLCanvasElement {
        const key = color.join(',');
        let sprite = this.sprites.get(key);
        if (!sprite) {
            sprite = createSoftDotSprite(effectColorToRgb(color), DOT_SPRITE_SIZE, DOT_SPRITE_STOPS);
            this.sprites.set(key, sprite);
        }
        return sprite;
    }
}

/** Builds an effect's emitter, resolving the sprites it stamps once, up front. */
function createEmitter(effect: SkillTreeEffect, spriteFor: SpriteFor): Emitter {
    const rng = new SeededRng(`skill-tree-effect:${effect.id}`);
    switch (effect.kind) {
        case 'halo':
            return createHaloEmitter(effect, rng, spriteFor);
        case 'glint':
            return createGlintEmitter(effect, rng, spriteFor);
        case 'blaze':
            return createBlazeEmitter(effect, rng, spriteFor);
        case 'orbit':
            return createOrbitEmitter(effect, rng, spriteFor);
        case 'burst':
            return createBurstEmitter(effect, rng, spriteFor);
    }
}

function createHaloEmitter(effect: SkillTreeHaloEffect, rng: SeededRng, spriteFor: SpriteFor): Emitter {
    const phase = rng.random() * Math.PI * 2;
    const sprite = spriteFor(effect.color);
    return {
        delay: effect.delay,
        emit: (seconds, write) => {
            const breath = Math.sin(seconds * HALO_BREATH_HZ * Math.PI * 2 + phase);
            write(effect.center.x, effect.center.y, effect.radius * 2 * (1 + 0.04 * breath), sprite, effect.alpha * (1 + 0.18 * breath));
        },
    };
}

function createGlintEmitter(effect: SkillTreeGlintEffect, rng: SeededRng, spriteFor: SpriteFor): Emitter {
    const path = new PathSampler(effect.path, effect.closed);
    const start = rng.random() * path.length;
    const glow = spriteFor(effect.color);
    const core = spriteFor(mixColor(effect.color, WHITE, 0.5));
    return {
        delay: effect.delay,
        emit: (seconds, write) => {
            if (path.length <= 0) return;
            for (let index = 0; index < effect.count; index += 1) {
                const distance = wrap(start + seconds * effect.speed + (index * path.length) / effect.count, path.length);
                const point = path.at(distance);
                const fade = effect.closed ? 1 : Math.sin((Math.PI * distance) / path.length);
                write(point.x, point.y, effect.size * 3, glow, effect.alpha * 0.35 * fade);
                write(point.x, point.y, effect.size, core, effect.alpha * fade);
            }
        },
    };
}

function createBlazeEmitter(effect: SkillTreeBlazeEffect, rng: SeededRng, spriteFor: SpriteFor): Emitter {
    const path = new PathSampler(effect.path, effect.closed);
    const flame = spriteFor(effect.color);
    const hot = spriteFor(effect.hotColor);
    const flames = path.length > 0 ? Math.max(3, Math.ceil(path.length / effect.spacing)) : 0;
    const flameSteps = effect.closed ? flames : Math.max(1, flames - 1);
    const flicker = Array.from({ length: flames }, () => ({
        fast: 3 + rng.random() * 4,
        slow: 0.8 + rng.random() * 1.2,
        fastPhase: rng.random() * Math.PI * 2,
        slowPhase: rng.random() * Math.PI * 2,
    }));
    const embers = Array.from({ length: path.length > 0 ? effect.embers : 0 }, () => ({
        cycle: 1.4 + rng.random() * 0.8,
        offset: rng.random() * 3,
        seed: rng.random(),
    }));
    return {
        delay: effect.delay,
        emit: (seconds, write) => {
            flicker.forEach((phases, index) => {
                const point = path.at((index * path.length) / flameSteps);
                const strength = 0.6 + 0.25 * Math.sin(seconds * phases.fast + phases.fastPhase) + 0.15 * Math.sin(seconds * phases.slow + phases.slowPhase);
                write(point.x, point.y, effect.size * (0.85 + 0.3 * strength), flame, effect.alpha * strength);
                write(point.x, point.y, effect.size * 0.35, hot, effect.alpha * 1.6 * strength);
            });
            for (const ember of embers) {
                const age = (seconds + ember.offset) / ember.cycle;
                const life = age - Math.floor(age);
                // Each new life starts somewhere else along the path.
                const origin = path.at(wrap(ember.seed + Math.floor(age) * 0.618, 1) * path.length);
                const outX = origin.x - effect.center.x;
                const outY = origin.y - effect.center.y;
                const outLength = Math.hypot(outX, outY) || 1;
                write(
                    origin.x + (outX / outLength) * EMBER_RISE * life,
                    origin.y + (outY / outLength) * EMBER_RISE * life,
                    effect.size * 0.3 * (1 - 0.5 * life),
                    hot,
                    effect.alpha * 3 * (1 - life) * Math.min(1, life * 5),
                );
            }
        },
    };
}

function createOrbitEmitter(effect: SkillTreeOrbitEffect, rng: SeededRng, spriteFor: SpriteFor): Emitter {
    const sprite = spriteFor(effect.color);
    const motes = Array.from({ length: effect.count }, () => ({
        radius: effect.radius * (0.75 + 0.4 * rng.random()),
        angle: rng.random() * Math.PI * 2,
        spin: (0.08 + 0.18 * rng.random()) * (rng.random() < 0.5 ? -1 : 1),
        bobPhase: rng.random() * Math.PI * 2,
        twinkle: 0.6 + rng.random() * 1.4,
        twinklePhase: rng.random() * Math.PI * 2,
        size: effect.size * (0.6 + 0.8 * rng.random()),
    }));
    return {
        delay: effect.delay,
        emit: (seconds, write) => {
            for (const mote of motes) {
                const angle = mote.angle + mote.spin * seconds;
                const radius = mote.radius + 3 * Math.sin(seconds * 0.7 + mote.bobPhase);
                const twinkle = 0.5 + 0.5 * Math.sin(seconds * mote.twinkle + mote.twinklePhase);
                write(
                    effect.center.x + radius * Math.cos(angle),
                    effect.center.y + radius * Math.sin(angle),
                    mote.size,
                    sprite,
                    effect.alpha * twinkle,
                );
            }
        },
    };
}

function createBurstEmitter(effect: SkillTreeBurstEffect, rng: SeededRng, spriteFor: SpriteFor): Emitter {
    const ring = spriteFor(effect.color);
    const hot = spriteFor(effect.hotColor);
    const sparks = Array.from({ length: effect.sparks }, () => ({
        angle: rng.random() * Math.PI * 2,
        reach: 1.2 + rng.random() * 0.7,
        size: 0.06 + rng.random() * 0.06,
    }));
    return {
        // The burst times itself; it never takes the delayed fade-in.
        delay: undefined,
        emit: (_seconds, write, sinceSet) => {
            const progress = (sinceSet - (effect.delay ?? 0)) / effect.duration;
            if (progress < 0 || progress > 1) return;
            const { x, y } = effect.center;
            const eased = 1 - (1 - progress) ** 3;
            const flash = Math.max(0, 1 - progress / 0.3);
            write(x, y, effect.radius * 1.4, hot, effect.alpha * 0.7 * flash);
            const ringRadius = effect.radius * eased;
            for (let index = 0; index < BURST_RING_DOTS; index += 1) {
                const angle = (index / BURST_RING_DOTS) * Math.PI * 2;
                write(x + ringRadius * Math.cos(angle), y + ringRadius * Math.sin(angle), effect.radius * 0.22, ring, effect.alpha * (1 - progress));
            }
            for (const spark of sparks) {
                const reach = effect.radius * spark.reach * eased;
                write(x + reach * Math.cos(spark.angle), y + reach * Math.sin(spark.angle), effect.radius * spark.size, hot, effect.alpha * (1 - progress) ** 2 * 1.5);
            }
        },
    };
}

/** Samples a polyline by distance along it. */
class PathSampler {
    readonly length: number;
    private readonly points: readonly Vector2[];
    private readonly distances: number[];
    /** The point `at` returns, reused so sampling allocates nothing per frame. */
    private readonly scratch: Vector2 = { x: 0, y: 0 };

    constructor(points: readonly Vector2[], closed: boolean) {
        this.points = closed && points.length > 1 ? [...points, points[0]!] : points;
        this.distances = [0];
        for (let index = 1; index < this.points.length; index += 1) {
            const from = this.points[index - 1]!;
            const to = this.points[index]!;
            this.distances.push(this.distances[index - 1]! + Math.hypot(to.x - from.x, to.y - from.y));
        }
        this.length = this.distances[this.distances.length - 1] ?? 0;
    }

    /**
     * The point `distance` along the path. It returns the same object every
     * call, overwritten each time: read it before sampling again.
     */
    at(distance: number): Readonly<Vector2> {
        const { points, distances, scratch } = this;
        if (points.length === 0) {
            scratch.x = 0;
            scratch.y = 0;
            return scratch;
        }
        // The first segment end at or past `distance` (the last segment if none is).
        let index = 1;
        let high = distances.length - 1;
        while (index < high) {
            const middle = (index + high) >> 1;
            if (distances[middle]! < distance) index = middle + 1;
            else high = middle;
        }
        const from = points[index - 1] ?? points[0]!;
        const to = points[index] ?? from;
        const span = distances[index]! - distances[index - 1]!;
        const t = span > 0 ? clamp01((distance - distances[index - 1]!) / span) : 0;
        scratch.x = from.x + (to.x - from.x) * t;
        scratch.y = from.y + (to.y - from.y) * t;
        return scratch;
    }
}

function mixColor(from: SkillTreeEffectColor, to: SkillTreeEffectColor, amount: number): SkillTreeEffectColor {
    return [
        from[0] + (to[0] - from[0]) * amount,
        from[1] + (to[1] - from[1]) * amount,
        from[2] + (to[2] - from[2]) * amount,
    ];
}
